/**
 * Sprint 23.7 — `/welcome/theme` (step 2). The smart-default theme picker: five
 * `<ThemeCard variant="picker">`s with the current theme (default Workforce)
 * pre-selected. Picking a card just changes the local selection; "Sounds good"
 * persists it to `workspace_context.language_theme` (applying it live, Q21) and
 * advances to the hire step.
 */

import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useLanguageTheme } from "../../hooks/use-language-theme";
import { useThemedLabels } from "../../hooks/use-themed-labels";
import { LANGUAGE_THEMES, type LanguageTheme } from "../../lib/themed-labels";
import { ThemeCard } from "../theme/ThemeCard";
import { PrimaryButton, StepHeading, StepLabel, StepSpacer } from "./welcome-ui";

export function WelcomeThemeStep() {
  const navigate = useNavigate();
  const labels = useThemedLabels();
  const [searchParams] = useSearchParams();
  const { theme, setTheme } = useLanguageTheme();

  const intent = searchParams.get("intent") ?? "";
  const [selected, setSelected] = useState<LanguageTheme>(theme);

  const onConfirm = () => {
    if (selected !== theme) setTheme(selected); // persist + apply live
    const q = intent ? `?intent=${encodeURIComponent(intent)}` : "";
    navigate(`/welcome/hire${q}`);
  };

  return (
    <>
      <StepLabel>Step 2 of 3</StepLabel>
      <StepHeading>{labels.themePickerTitle}</StepHeading>
      <p style={{ margin: 0, fontSize: 12, color: "var(--color-text-secondary)", lineHeight: 1.55 }}>
        Change anytime in Settings.
      </p>

      <div role="radiogroup" aria-label="Language theme" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {LANGUAGE_THEMES.map((key) => (
          <ThemeCard key={key} theme={key} variant="picker" selected={selected === key} onPick={() => setSelected(key)} />
        ))}
      </div>

      <StepSpacer />

      <div style={{ display: "flex", justifyContent: "center" }}>
        <PrimaryButton onClick={onConfirm}>Sounds good</PrimaryButton>
      </div>
    </>
  );
}
